export interface BreadcrumbPath {
  label: string;
  path: string;
}

import { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IMediaItem, IListLibraryMediaResponse } from 'modules/media/interfaces/api';

type MutateFn = (path: string) => void;

const ROOT_PATH = ' ';

const encodePath = (path: string) => encodeURIComponent(path);

const decodePath = (value: string | null) =>
  value ? decodeURIComponent(value) : ROOT_PATH;

export const useMediaExplorer = (
  data: IListLibraryMediaResponse | undefined,
  mutate: MutateFn
) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentPath = useMemo(() => {
    return decodePath(searchParams.get('path'));
  }, [searchParams]);

  useEffect(() => {
    mutate(currentPath);
  }, [currentPath]);

  const openFolder = (folder: IMediaItem) => {
    navigate(
      `/media/library/detail?path=${encodePath(folder.path)}`
    );
  };

  const onBack = () => {
    if (currentPath === ROOT_PATH) {
      navigate('/media');
      return;
    }

    const segments = currentPath.split('/').filter(Boolean);

    if (segments.length <= 1) {
      navigate('/media');
    } else {
      const newPath = segments.slice(0, -1).join('/');
      setSearchParams({
        path: encodePath(newPath),
      });
    }
  };

  const goRoot = () => {
    navigate('/media');
  };

  const breadcrumbItems = useMemo(() => {
    if (currentPath === ROOT_PATH) return [];

    const segments = currentPath.split('/').filter(Boolean);

    return segments.map((segment, index) => ({
      label: segment,
      path: segments.slice(0, index + 1).join('/'),
    }));
  }, [currentPath]);

  const goToPath = (path: string) => {
    navigate(`/media/detail?path=${encodePath(path)}`);
  };

  return {
    items: data?.items ?? [],
    currentPath,
    pathStack:breadcrumbItems,
    openFolder,
    onBack,
    goRoot,
    goToPath,
  };
};
